(() => {
  window.SBHeaderInitializers = window.SBHeaderInitializers || [];

  window.SBHeaderInitializers.push((context) => {
    const { header, signal, addRuntimeEventListener, findHeaderRef, onAbort, shared } = context;

    const isDesktopViewport = shared.isDesktopViewport || (() => window.matchMedia('(min-width: 990px)').matches && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
    const isMobileViewport = shared.isMobileViewport || (() => !isDesktopViewport());
    const headerInnerElement = findHeaderRef(header, 'headerInner', '.header__inner');
    const desktopMenu = findHeaderRef(header, 'headerMenu', '.header__menu');
    const desktopMenuTrack = findHeaderRef(header, 'headerMenuTrack', '.header__menu-track');
    const desktopMenuItems = desktopMenu ? Array.from(desktopMenu.querySelectorAll('.header__menu-item')) : [];
    const desktopPrimaryMenus = desktopMenu ? Array.from(desktopMenu.querySelectorAll('.header__primary-menu')) : [];
    const desktopPrimaryMenuPanel = header.querySelector('.header__primary-menu-panel');
    const desktopBrand = header.querySelector('.header__brand');
    const desktopRightIcons = header.querySelector('.header__icons--right');
    const getDesktopAccessorySurface = () => document.querySelector('[data-sb-header-accessory-surface]');
    const getDesktopAccessoryShell = () => document.querySelector('[data-sb-header-accessory-shell]');

    const navWrapSideGap = 24;
    const navWrapMinimumItemGap = 12;
    const navWrapMaximumItemGap = 24;
    let navLayoutRaf = null;
    let navLayoutObserver = null;
    let dropdownTrackBoundsCache = null;

    const clearDesktopNavLayoutClasses = () => {
      header.classList.remove('is-nav-wrap', 'is-nav-compacted');
      header.style.removeProperty('--sb-desktop-menu-gap');
      desktopMenuItems.forEach((menuItem) => {
        menuItem.style.removeProperty('--sb-desktop-menu-item-width');
      });
    };

    const syncDesktopNavSafeArea = () => {
      if (!desktopMenu || !desktopMenuTrack || !desktopBrand || !desktopRightIcons || !isDesktopViewport()) {
        header.style.removeProperty('--sb-desktop-menu-safe-inset');
        return;
      }

      const menuRect = desktopMenu.getBoundingClientRect();
      const brandRect = desktopBrand.getBoundingClientRect();
      const utilityRect = desktopRightIcons.getBoundingClientRect();
      const safeStart = Math.max(0, brandRect.right - menuRect.left + navWrapSideGap);
      const safeEnd = Math.max(0, menuRect.right - utilityRect.left + navWrapSideGap);
      header.style.setProperty('--sb-desktop-menu-safe-inset', `${Math.max(safeStart, safeEnd)}px`);
    };

    const syncPrimaryMenuSurface = ({ expanded = header.classList.contains('is-primary-mega-open') } = {}) => {
      const primaryMenu = desktopPrimaryMenus[0];
      const desktopAccessorySurface = getDesktopAccessorySurface();
      const desktopAccessoryShell = getDesktopAccessoryShell();
      if (
        !isDesktopViewport()
        || !(headerInnerElement instanceof HTMLElement)
        || !(desktopAccessorySurface instanceof HTMLElement)
        || !(desktopAccessoryShell instanceof HTMLElement)
        || !(primaryMenu instanceof HTMLElement)
      ) {
        delete header.dataset.primaryMenuHandoff;
        header.style.removeProperty('--sb-primary-menu-content-closed-overlap');
        if (desktopAccessoryShell instanceof HTMLElement) {
          desktopAccessoryShell.style.removeProperty('height');
        }
        return;
      }

      const headerInnerRect = headerInnerElement.getBoundingClientRect();
      const stickyBarRect = desktopAccessorySurface.getBoundingClientRect();
      const restHeight = stickyBarRect.bottom - headerInnerRect.top;
      if (
        !Number.isFinite(restHeight)
        || restHeight <= 0
      ) {
        delete header.dataset.primaryMenuHandoff;
        header.style.removeProperty('--sb-primary-menu-content-closed-overlap');
        desktopAccessoryShell.style.removeProperty('height');
        return;
      }

      const primaryMenuPanel = primaryMenu.querySelector('.header__primary-menu-panel');
      const panelRect = primaryMenuPanel instanceof HTMLElement
        ? primaryMenuPanel.getBoundingClientRect()
        : null;
      const activePanel = expanded
        ? header.querySelector('.header__menu-item.has-dropdown.is-open .header__primary-menu-panel')
        : null;
      const activePanelRect = activePanel instanceof HTMLElement
        ? activePanel.getBoundingClientRect()
        : null;

      const contentClosedOverlap = panelRect
        ? Math.max(0, Math.min(panelRect.height, stickyBarRect.bottom - panelRect.top))
        : null;
      const targetHeight = activePanelRect && Number.isFinite(activePanelRect.bottom)
        ? Math.max(restHeight, activePanelRect.bottom - headerInnerRect.top)
        : null;

      /* Apply all geometry-dependent writes after the complete read phase.
         This avoids forcing a second layout while opening or closing a menu. */
      header.dataset.primaryMenuHandoff = 'accessory';
      header.style.removeProperty('--sb-primary-menu-content-closed-overlap');
      if (contentClosedOverlap != null) {
        /* On fused accessory pages the shell closes at the bottom of the
           sticky bar, not at the nav seam. Use that same physical edge for
           content clipping so images cannot retract ahead of the surface. */
        header.style.setProperty(
          '--sb-primary-menu-content-closed-overlap',
          `${contentClosedOverlap.toFixed(2)}px`,
        );
      }
      if (!expanded) {
        desktopAccessoryShell.style.removeProperty('height');
        return;
      }

      if (targetHeight == null) return;
      desktopAccessoryShell.style.height = `${targetHeight.toFixed(2)}px`;
    };

    const compactWrappedNavItems = () => {
      const paintedWidths = desktopMenuItems.map((menuItem) => {
        const menuLink = menuItem.querySelector('.header__menu-link');
        if (!(menuLink instanceof HTMLElement)) return null;

        const textRange = document.createRange();
        textRange.selectNodeContents(menuLink);
        const lineWidths = Array.from(textRange.getClientRects(), (rect) => rect.width)
          .filter((width) => Number.isFinite(width) && width > 0);
        textRange.detach();
        if (lineWidths.length === 0) return null;

        /* Flexbox allocates wrapping items from their unbroken width, which
           leaves invisible space after a line wraps. Pin the item to its
           widest painted line so the declared gap is also the visual gap. */
        return Math.ceil(Math.max(...lineWidths) + 0.5);
      });

      paintedWidths.forEach((paintedWidth, index) => {
        if (!paintedWidth) return;
        desktopMenuItems[index].style.setProperty('--sb-desktop-menu-item-width', `${paintedWidth}px`);
      });

      const totalPaintedWidth = paintedWidths.reduce((total, width) => total + (width || 0), 0);
      const itemGapCount = Math.max(0, paintedWidths.length - 1);
      if (desktopMenuTrack && itemGapCount > 0) {
        const trackWidth = desktopMenuTrack.getBoundingClientRect().width;
        const availableItemGap = (trackWidth - totalPaintedWidth) / itemGapCount;
        const responsiveItemGap = Math.max(
          navWrapMinimumItemGap,
          Math.min(navWrapMaximumItemGap, availableItemGap),
        );
        /* Round down to keep the measured safety buffer intact on fractional
           device pixels while using all useful space up to the normal gap. */
        const safeItemGap = Math.floor(responsiveItemGap * 4) / 4;
        header.style.setProperty('--sb-desktop-menu-gap', `${safeItemGap}px`);
      }
      header.classList.add('is-nav-compacted');
    };

    const syncDesktopHeaderMeasurements = () => {
      if (!headerInnerElement) return;
      const headerInnerRect = headerInnerElement.getBoundingClientRect();
      const headerInnerWidth = headerInnerRect.width;
      if (!Number.isFinite(headerInnerWidth) || headerInnerWidth <= 0) return;
      header.style.setProperty('--sb-header-inner-width', `${headerInnerWidth}px`);
      syncDesktopNavSafeArea();
      syncPrimaryMenuSurface();

      if (!desktopPrimaryMenuPanel || !isDesktopViewport()) return;
      if (header.classList.contains('is-primary-open') || header.classList.contains('is-primary-closing')) return;
      const currentOverlap = Number.parseFloat(
        window.getComputedStyle(header).getPropertyValue('--sb-primary-menu-closed-overlap'),
      );
      const closedPanelTop = desktopPrimaryMenuPanel.getBoundingClientRect().top;
      const closedSurfaceBottom = closedPanelTop + currentOverlap;
      const overlapCorrection = headerInnerRect.bottom - closedSurfaceBottom;
      if (!Number.isFinite(currentOverlap) || !Number.isFinite(overlapCorrection)) return;
      if (Math.abs(overlapCorrection) > 4) return;
      header.style.setProperty('--sb-primary-menu-closed-overlap', `${currentOverlap + overlapCorrection}px`);
    };

    const getDesktopNavRects = () => {
      if (!desktopMenu || !desktopBrand || !desktopRightIcons) return null;
      // The menu container spans the whole nav box (desktop redesign), so
      // overlap is measured from the painted links, not the wrapper or a
      // flex item that may already be narrower than its text.
      const firstItem = desktopMenuItems[0];
      const lastItem = desktopMenuItems[desktopMenuItems.length - 1];
      const firstLink = firstItem?.querySelector('.header__menu-link');
      const lastLink = lastItem?.querySelector('.header__menu-link');
      const menuRect = firstItem && lastItem
        ? {
          left: (firstLink || firstItem).getBoundingClientRect().left,
          right: (lastLink || lastItem).getBoundingClientRect().right,
        }
        : desktopMenu.getBoundingClientRect();
      return {
        menuRect,
        brandRect: desktopBrand.getBoundingClientRect(),
        utilityRect: desktopRightIcons.getBoundingClientRect(),
      };
    };

    const hasDesktopNavOverlap = () => {
      const rects = getDesktopNavRects();
      if (!rects) return false;
      return (
        rects.menuRect.left < (rects.brandRect.right + navWrapSideGap)
        || rects.menuRect.right > (rects.utilityRect.left - navWrapSideGap)
      );
    };

    const applyDesktopNavLayout = () => {
      syncDesktopHeaderMeasurements();

      if (!isDesktopViewport() || !desktopMenu || desktopMenuItems.length === 0) {
        clearDesktopNavLayoutClasses();
        return;
      }

      clearDesktopNavLayoutClasses();
      if (!hasDesktopNavOverlap()) return;

      header.classList.add('is-nav-wrap');
      compactWrappedNavItems();
    };

    const scheduleDesktopNavLayout = () => {
      if (signal.aborted) return;
      dropdownTrackBoundsCache = null;
      if (navLayoutRaf) window.cancelAnimationFrame(navLayoutRaf);
      navLayoutRaf = window.requestAnimationFrame(() => {
        navLayoutRaf = null;
        applyDesktopNavLayout();
      });
    };

    addRuntimeEventListener(window, 'resize', scheduleDesktopNavLayout, { passive: true });
    scheduleDesktopNavLayout();

    if (document.fonts && typeof document.fonts.ready === 'object') {
      document.fonts.ready.then(() => {
        scheduleDesktopNavLayout();
      });
    }

    if (window.ResizeObserver && headerInnerElement) {
      navLayoutObserver = new ResizeObserver(() => {
        scheduleDesktopNavLayout();
      });
      navLayoutObserver.observe(headerInnerElement);
      if (desktopBrand) navLayoutObserver.observe(desktopBrand);
      if (desktopRightIcons) navLayoutObserver.observe(desktopRightIcons);
      if (desktopMenuTrack) navLayoutObserver.observe(desktopMenuTrack);
      /* The accessory surface is deliberately excluded. Its height changes on
         every frame of the fused-nav collapse, while the desktop menu geometry
         it feeds is synchronized directly on menu open/close and viewport
         changes. Observing it here made the scroll animation schedule a second
         layout pass on every frame. */
    }

    const dropdownMenuItems = header.querySelectorAll('.header__menu-item.has-dropdown');
    const plainMenuItems = header.querySelectorAll('.header__menu-item:not(.has-dropdown)');
    const primaryBackdrops = Array.from(document.querySelectorAll('[data-header-primary-backdrop]'));

    if (dropdownMenuItems.length > 0) {
      let activeDropdownItem = null;
      let closingDropdownItem = null;
      let localizationDropdownOpen = false;
      let searchShortcutOpen = false;
      let closeMenuTimer = null;
      let primarySwitchFrame = null;
      let primaryClosingTimer = null;
      let primaryBackdropOpenFrame = null;
      let hoverOpenTimer = null;
      let hoverOpenCandidateItem = null;
      let hoverOpenPointerStartX = 0;
      let hoverOpenPointerStartY = 0;
      let lastMouseMoveAt = 0;
      let pointerX = 0;
      let pointerY = 0;

      const menuSafePadding = 8;
      const primaryHoverOpenDelayMs = 36;
      const primaryHoverSettleDelayMs = 34;
      const primaryHoverMoveThresholdPx = 10;

      const headerInner = header.querySelector('.header__inner');
      const headerMenu = header.querySelector('.header__menu');

      const dropdownMenuLinks = Array.from(dropdownMenuItems)
        .map((menuItem) => menuItem.querySelector('.header__menu-link'))
        .filter(Boolean);

      const prioritizedPrimaryMenuItems = new WeakSet();

      const triggerPrimaryMenuPreload = (menuItem) => {
        if (!(menuItem instanceof HTMLElement) || prioritizedPrimaryMenuItems.has(menuItem)) return;
        prioritizedPrimaryMenuItems.add(menuItem);
        if (typeof shared.preloadPrimaryMenuImages === 'function') {
          shared.preloadPrimaryMenuImages(menuItem, { highPriority: true });
        }
      };

      const clearCloseMenuTimer = () => {
        if (!closeMenuTimer) return;
        window.clearTimeout(closeMenuTimer);
        closeMenuTimer = null;
      };

      const clearHoverOpenTimer = () => {
        if (hoverOpenTimer) window.clearTimeout(hoverOpenTimer);
        hoverOpenTimer = null;
        hoverOpenCandidateItem = null;
      };

      const clearPrimarySwitchClip = () => {
        dropdownMenuItems.forEach((menuItem) => {
          const menu = menuItem.querySelector('.header__primary-menu');
          if (!(menu instanceof HTMLElement)) return;
          menu.classList.remove('is-primary-height-reveal');
          menu.style.removeProperty('--sb-primary-menu-switch-clip-bottom');
        });
      };

      const clearPrimarySwitchFrame = () => {
        if (!primarySwitchFrame) return;
        window.cancelAnimationFrame(primarySwitchFrame);
        primarySwitchFrame = null;
        clearPrimarySwitchClip();
      };

      const clearPrimaryClosingTimer = () => {
        if (primaryClosingTimer) window.clearTimeout(primaryClosingTimer);
        primaryClosingTimer = null;
      };

      const getCssDurationMs = (propertyName, fallbackMs) => {
        const value = window.getComputedStyle(header).getPropertyValue(propertyName).trim();
        const parsedValue = Number.parseFloat(value);
        if (!Number.isFinite(parsedValue)) return fallbackMs;
        return value.endsWith('ms') ? parsedValue : parsedValue * 1000;
      };
      const primaryMenuCloseDurationMs = getCssDurationMs('--sb-primary-menu-close-duration', 180);

      const clearPrimaryBackdropOpenFrame = () => {
        if (!primaryBackdropOpenFrame) return;
        window.cancelAnimationFrame(primaryBackdropOpenFrame);
        primaryBackdropOpenFrame = null;
      };

      const schedulePrimaryBackdropOpen = () => {
        clearPrimaryBackdropOpenFrame();
        primaryBackdrops.forEach((backdrop) => backdrop.classList.remove('is-active'));
        primaryBackdropOpenFrame = window.requestAnimationFrame(() => {
          primaryBackdropOpenFrame = null;
          primaryBackdrops.forEach((backdrop) => backdrop.classList.add('is-active'));
        });
      };

      const endPrimarySwitchMode = () => {
        header.classList.remove('is-primary-switching');
        dropdownMenuItems.forEach((menuItem) => {
          const menu = menuItem.querySelector('.header__primary-menu.is-primary-height-reveal');
          if (menu instanceof HTMLElement) window.getComputedStyle(menu).clipPath;
        });
        clearPrimarySwitchClip();
      };

      const triggerPrimarySwitchMode = (fromItem, toItem) => {
        if (!fromItem || !toItem) return;
        const fromMenu = fromItem.querySelector('.header__primary-menu');
        const toMenu = toItem.querySelector('.header__primary-menu');
        if (!fromMenu || !toMenu) return;
        clearPrimarySwitchFrame();

        const accessoryShell = getDesktopAccessoryShell();
        if (header.dataset.primaryMenuHandoff === 'accessory' && accessoryShell instanceof HTMLElement) {
          const fromMenuRect = fromMenu.getBoundingClientRect();
          const toMenuRect = toMenu.getBoundingClientRect();
          const shellRect = accessoryShell.getBoundingClientRect();
          const visibleBottom = Math.min(fromMenuRect.bottom, shellRect.bottom);
          const clipBottom = Math.max(0, toMenuRect.bottom - visibleBottom);
          if (clipBottom > 0.5) {
            toMenu.style.setProperty('--sb-primary-menu-switch-clip-bottom', `${clipBottom.toFixed(2)}px`);
            toMenu.classList.add('is-primary-height-reveal');
            /* Commit the clipped state before the active item changes so the
               switch can hold and paint this exact starting boundary. */
            window.getComputedStyle(toMenu).clipPath;
          }
        }

        header.classList.add('is-primary-switching');
      };

      const commitPrimarySwitchMode = () => {
        const activeMenu = header.querySelector(
          '.header__menu-item.has-dropdown.is-open > .header__primary-menu',
        );
        if (activeMenu instanceof HTMLElement) {
          /* The target must be active before this read. Otherwise the clipped
             frame can be removed before the browser ever paints it. */
          window.getComputedStyle(activeMenu).clipPath;
        }
        primarySwitchFrame = window.requestAnimationFrame(() => {
          primarySwitchFrame = window.requestAnimationFrame(() => {
            endPrimarySwitchMode();
            syncPrimaryMenuSurface({ expanded: true });
            primarySwitchFrame = null;
          });
        });
      };

      const updatePrimaryOpenState = () => {
        const isPrimaryOpen = Boolean(activeDropdownItem || localizationDropdownOpen || searchShortcutOpen);
        const isMegaMenuOpen = Boolean(activeDropdownItem);
        const wasPrimaryOpen = header.classList.contains('is-primary-open');
        const wasMegaMenuOpen = header.classList.contains('is-primary-mega-open');
        if (isMobileViewport()) {
          clearPrimaryClosingTimer();
          clearPrimaryBackdropOpenFrame();
          header.classList.remove(
            'is-primary-closing',
            'is-primary-mega-open',
            'is-primary-mega-closing',
          );
          if (activeDropdownItem) activeDropdownItem.classList.remove('is-open');
          activeDropdownItem = null;
          closingDropdownItem = null;
          localizationDropdownOpen = false;
          header.classList.remove('is-primary-open');
          syncPrimaryMenuSurface({ expanded: false });
          clearPrimarySwitchFrame();
          header.classList.remove('is-primary-switching');
          primaryBackdrops.forEach((backdrop) => backdrop.classList.remove('is-active'));
          if (typeof shared.syncPageScrollLock === 'function') shared.syncPageScrollLock();
          return;
        }

        if (isPrimaryOpen) {
          if (!wasPrimaryOpen) {
            window.SBHeaderNav?.expandNavCollapseForOverlay?.();
            dropdownTrackBoundsCache = null;
          }
          clearPrimaryClosingTimer();
          header.classList.remove('is-primary-closing');
          header.classList.add('is-primary-open');

          if (isMegaMenuOpen) {
            closingDropdownItem = null;
            header.classList.remove('is-primary-mega-closing');
            header.classList.add('is-primary-mega-open');
            if (!header.classList.contains('is-primary-switching')) {
              syncPrimaryMenuSurface({ expanded: true });
            }
          } else if (wasMegaMenuOpen || closingDropdownItem) {
            header.classList.remove('is-primary-mega-open');
            header.classList.add('is-primary-mega-closing');
            syncPrimaryMenuSurface({ expanded: false });
            primaryClosingTimer = window.setTimeout(() => {
              header.classList.remove('is-primary-mega-closing');
              closingDropdownItem = null;
              primaryClosingTimer = null;
            }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : primaryMenuCloseDurationMs);
          }

          schedulePrimaryBackdropOpen();
        } else {
          clearPrimaryBackdropOpenFrame();
          header.classList.remove('is-primary-open');
          header.classList.remove('is-primary-mega-open');
          clearPrimaryClosingTimer();
          if (!wasPrimaryOpen) {
            header.classList.remove('is-primary-closing');
            header.classList.remove('is-primary-mega-closing');
            closingDropdownItem = null;
            syncPrimaryMenuSurface({ expanded: false });
            primaryBackdrops.forEach((backdrop) => backdrop.classList.remove('is-active'));
            if (typeof shared.syncPageScrollLock === 'function') shared.syncPageScrollLock();
            return;
          }
          header.classList.add('is-primary-closing');
          if (wasMegaMenuOpen || closingDropdownItem) {
            header.classList.add('is-primary-mega-closing');
          }
          syncPrimaryMenuSurface({ expanded: false });
          const completePrimaryClosing = () => {
            if (primaryClosingTimer) window.clearTimeout(primaryClosingTimer);
            header.classList.remove('is-primary-closing', 'is-primary-mega-closing');
            closingDropdownItem = null;
            syncPrimaryMenuSurface({ expanded: false });
            primaryClosingTimer = null;
            if (typeof shared.syncPageScrollLock === 'function') shared.syncPageScrollLock();
          };

          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          primaryClosingTimer = window.setTimeout(
            completePrimaryClosing,
            reduceMotion ? 0 : primaryMenuCloseDurationMs,
          );
          primaryBackdrops.forEach((backdrop) => backdrop.classList.remove('is-active'));
        }

        if (typeof shared.syncPageScrollLock === 'function') shared.syncPageScrollLock();
      };

      const handleLocalizationDropdownToggle = (event) => {
        const nextOpen = Boolean(event && event.detail && event.detail.open);
        localizationDropdownOpen = nextOpen;

        if (localizationDropdownOpen && searchShortcutOpen) {
          document.dispatchEvent(new CustomEvent('sb:header-search-shortcut:request-close'));
        }

        if (localizationDropdownOpen && activeDropdownItem) {
          setActiveDropdownItem(null);
          return;
        }

        updatePrimaryOpenState();
      };

      const handleSearchShortcutToggle = (event) => {
        const nextOpen = Boolean(event && event.detail && event.detail.open);
        searchShortcutOpen = nextOpen;

        if (searchShortcutOpen) {
          clearHoverOpenTimer();
          clearCloseMenuTimer();

          if (activeDropdownItem) setActiveDropdownItem(null);

          if (localizationDropdownOpen) {
            localizationDropdownOpen = false;
            document.dispatchEvent(new CustomEvent('sb:header-localization:request-close'));
          }

        }

        updatePrimaryOpenState();
      };

      addRuntimeEventListener(document, 'sb:header-localization:toggle', handleLocalizationDropdownToggle);
      addRuntimeEventListener(document, 'sb:header-search-shortcut:toggle', handleSearchShortcutToggle);

      const setActiveDropdownItem = (nextItem) => {
        if (activeDropdownItem === nextItem) return;
        const previousDropdownItem = activeDropdownItem;
        if (nextItem) {
          localizationDropdownOpen = false;
          document.dispatchEvent(new CustomEvent('sb:header-localization:request-close'));
          if (searchShortcutOpen) {
            document.dispatchEvent(new CustomEvent('sb:header-search-shortcut:request-close'));
          }
          document.dispatchEvent(new CustomEvent('sb:blog-filter:request-close'));
        }
        const isPrimarySwitch = Boolean(activeDropdownItem && nextItem);
        if (isPrimarySwitch) triggerPrimarySwitchMode(activeDropdownItem, nextItem);
        else {
          clearPrimarySwitchFrame();
          header.classList.remove('is-primary-switching');
        }
        if (activeDropdownItem) activeDropdownItem.classList.remove('is-open');
        closingDropdownItem = previousDropdownItem && !nextItem ? previousDropdownItem : null;
        activeDropdownItem = nextItem || null;
        if (activeDropdownItem) activeDropdownItem.classList.add('is-open');
        if (isPrimarySwitch) commitPrimarySwitchMode();
        dropdownMenuItems.forEach((menuItem) => {
          const menuLink = menuItem.querySelector('.header__menu-link');
          if (menuLink) menuLink.setAttribute('aria-expanded', menuItem === activeDropdownItem ? 'true' : 'false');
        });
        updatePrimaryOpenState();
      };

      const closeActiveDropdown = () => {
        clearHoverOpenTimer();
        setActiveDropdownItem(null);
      };

      addRuntimeEventListener(document, 'keydown', (event) => {
        if (event.key !== 'Escape' || !activeDropdownItem) return;
        const focusTarget = activeDropdownItem.querySelector('.header__menu-link');
        // Restore focus while this item is still open: its focusin handler
        // would otherwise reopen the menu immediately after Escape.
        if (focusTarget && header.contains(document.activeElement)) focusTarget.focus();
        closeActiveDropdown();
      });

      const pointerStillCrossingMenuItem = () => {
        const moveAge = Date.now() - lastMouseMoveAt;
        const pointerTravel = Math.hypot(pointerX - hoverOpenPointerStartX, pointerY - hoverOpenPointerStartY);
        return moveAge < primaryHoverSettleDelayMs && pointerTravel > primaryHoverMoveThresholdPx;
      };

      const runHoverOpenIntent = () => {
        if (!hoverOpenCandidateItem) {
          clearHoverOpenTimer();
          return;
        }

        if (!hoverOpenCandidateItem.matches(':hover')) {
          clearHoverOpenTimer();
          return;
        }

        if (pointerStillCrossingMenuItem()) {
          hoverOpenTimer = window.setTimeout(runHoverOpenIntent, primaryHoverSettleDelayMs);
          return;
        }

        const targetItem = hoverOpenCandidateItem;
        clearHoverOpenTimer();
        clearCloseMenuTimer();
        setActiveDropdownItem(targetItem);
      };

      const scheduleHoverOpenIntent = (menuItem, event) => {
        if (!menuItem) return;
        clearHoverOpenTimer();
        clearCloseMenuTimer();
        if (activeDropdownItem) {
          setActiveDropdownItem(menuItem);
          return;
        }
        hoverOpenCandidateItem = menuItem;
        hoverOpenPointerStartX = event ? event.clientX : pointerX;
        hoverOpenPointerStartY = event ? event.clientY : pointerY;
        hoverOpenTimer = window.setTimeout(runHoverOpenIntent, primaryHoverOpenDelayMs);
      };

      const getDropdownTrackBounds = () => {
        if (!headerMenu || dropdownMenuLinks.length === 0) return null;
        const anchorRect = dropdownMenuLinks[0].getBoundingClientRect();
        if (
          dropdownTrackBoundsCache
          && Math.abs(anchorRect.left - dropdownTrackBoundsCache.anchorLeft) < 0.5
        ) {
          const verticalOffset = anchorRect.top - dropdownTrackBoundsCache.anchorTop;
          return {
            left: dropdownTrackBoundsCache.left,
            right: dropdownTrackBoundsCache.right,
            top: dropdownTrackBoundsCache.top + verticalOffset,
            bottom: dropdownTrackBoundsCache.bottom + verticalOffset,
          };
        }
        const linkRects = dropdownMenuLinks.map((menuLink) => menuLink.getBoundingClientRect());
        dropdownTrackBoundsCache = {
          anchorLeft: anchorRect.left,
          anchorTop: anchorRect.top,
          left: Math.min(...linkRects.map((rect) => rect.left)),
          right: Math.max(...linkRects.map((rect) => rect.right)),
          top: Math.min(...linkRects.map((rect) => rect.top)),
          bottom: Math.max(...linkRects.map((rect) => rect.bottom)),
        };
        return {
          left: dropdownTrackBoundsCache.left,
          right: dropdownTrackBoundsCache.right,
          top: dropdownTrackBoundsCache.top,
          bottom: dropdownTrackBoundsCache.bottom,
        };
      };

      const pointerInSafeCorridor = (menuItem) => {
        if (!menuItem || !headerInner) return false;
        const primaryMenu = menuItem.querySelector('.header__primary-menu');
        if (!primaryMenu) return false;
        const headerInnerRect = headerInner.getBoundingClientRect();
        const menuRect = primaryMenu.getBoundingClientRect();
        const corridorTop = Math.min(headerInnerRect.bottom, menuRect.top);
        const corridorBottom = Math.max(headerInnerRect.bottom, menuRect.top);
        const corridorLeft = menuRect.left;
        const corridorRight = menuRect.right;

        return (
          pointerX >= corridorLeft
          && pointerX <= corridorRight
          && pointerY >= corridorTop
          && pointerY <= corridorBottom
        );
      };

      const pointerInMenuStrip = () => {
        const trackBounds = getDropdownTrackBounds();
        if (!trackBounds) return false;
        return (
          pointerY >= trackBounds.top
          && pointerY <= trackBounds.bottom
          && pointerX >= trackBounds.left - menuSafePadding
          && pointerX <= trackBounds.right + menuSafePadding
        );
      };

      const scheduleCloseActiveDropdown = () => {
        clearCloseMenuTimer();
        closeMenuTimer = window.setTimeout(() => {
          const activePrimaryMenu = activeDropdownItem && activeDropdownItem.querySelector('.header__primary-menu');
          const activeItemHovered = Boolean(activeDropdownItem && activeDropdownItem.matches(':hover'));
          const primaryMenuHovered = Boolean(activePrimaryMenu && activePrimaryMenu.matches(':hover'));
          const inSafeCorridor = pointerInSafeCorridor(activeDropdownItem);
          const inMenuStrip = pointerInMenuStrip();

          if (activeItemHovered || primaryMenuHovered || inSafeCorridor || inMenuStrip) {
            scheduleCloseActiveDropdown();
            return;
          }

          closeActiveDropdown();
        }, 120);
      };

      dropdownMenuItems.forEach((menuItem) => {
        const menuLink = menuItem.querySelector('.header__menu-link');
        const primaryMenu = menuItem.querySelector('.header__primary-menu');

        if (menuLink) {
          addRuntimeEventListener(menuLink, 'mouseenter', (event) => {
            triggerPrimaryMenuPreload(menuItem);
            scheduleHoverOpenIntent(menuItem, event);
          });
          addRuntimeEventListener(menuLink, 'focusin', () => triggerPrimaryMenuPreload(menuItem));
          addRuntimeEventListener(menuLink, 'touchstart', () => triggerPrimaryMenuPreload(menuItem), { passive: true });
        }

        addRuntimeEventListener(menuItem, 'focusin', () => {
          clearHoverOpenTimer();
          clearCloseMenuTimer();
          setActiveDropdownItem(menuItem);
        });

        addRuntimeEventListener(menuItem, 'focusout', (event) => {
          if (menuItem.contains(event.relatedTarget)) return;
          window.requestAnimationFrame(() => {
            if (signal.aborted) return;
            if (!header.contains(document.activeElement)) closeActiveDropdown();
            else updatePrimaryOpenState();
          });
        });

        if (primaryMenu) {
          addRuntimeEventListener(primaryMenu, 'mouseenter', () => {
            clearHoverOpenTimer();
            clearCloseMenuTimer();
            setActiveDropdownItem(menuItem);
          });

          addRuntimeEventListener(primaryMenu, 'mouseleave', () => {
            scheduleCloseActiveDropdown();
          });
        }
      });

      plainMenuItems.forEach((menuItem) => {
        const menuLink = menuItem.querySelector('.header__menu-link');
        if (!menuLink) return;
        addRuntimeEventListener(menuLink, 'mouseenter', () => {
          clearHoverOpenTimer();
          clearCloseMenuTimer();
          closeActiveDropdown();
        });
      });

      addRuntimeEventListener(header, 'mouseenter', () => {
        clearCloseMenuTimer();
      });

      addRuntimeEventListener(header, 'mouseleave', () => {
        clearHoverOpenTimer();
        scheduleCloseActiveDropdown();
      });

      if (headerInner) {
        addRuntimeEventListener(headerInner, 'mousemove', (event) => {
          if (!activeDropdownItem) return;
          if (isMobileViewport()) return;
          if (activeDropdownItem.matches(':hover')) {
            clearCloseMenuTimer();
            return;
          }
          const trackBounds = getDropdownTrackBounds();
          if (!trackBounds) return;

          const aboveMenuStrip = event.clientY < (trackBounds.top - menuSafePadding);
          if (aboveMenuStrip) {
            clearCloseMenuTimer();
            closeActiveDropdown();
            return;
          }

          const withinMenuBand = event.clientY >= trackBounds.top && event.clientY <= trackBounds.bottom;
          const outsideMenuHorizontally = event.clientX < (trackBounds.left - menuSafePadding)
            || event.clientX > (trackBounds.right + menuSafePadding);
          if (!withinMenuBand || !outsideMenuHorizontally) return;
          clearCloseMenuTimer();
          closeActiveDropdown();
        });
      }

      addRuntimeEventListener(document, 'pointerdown', (event) => {
        if (header.contains(event.target)) return;
        clearHoverOpenTimer();
        clearCloseMenuTimer();
        closeActiveDropdown();
      });

      addRuntimeEventListener(document, 'pointermove', (event) => {
        pointerX = event.clientX;
        pointerY = event.clientY;
        if (event.pointerType !== 'mouse') return;
        lastMouseMoveAt = Date.now();
        if (activeDropdownItem || hoverOpenCandidateItem) return;
        const hoveredItem = event.target instanceof Element
          ? event.target.closest('.header__menu-item.has-dropdown')
          : null;
        if (!(hoveredItem instanceof HTMLElement) || !header.contains(hoveredItem)) return;
        triggerPrimaryMenuPreload(hoveredItem);
        scheduleHoverOpenIntent(hoveredItem, event);
      }, { passive: true });

      /* A visitor can move onto a navigation link while deferred scripts are
         still loading. In that case the native mouseenter happened before
         this initializer attached. Honor the current hover state once, using
         the normal intent delay, so the first deliberate hover is not lost. */
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        const initiallyHoveredItem = header.querySelector('.header__menu-item.has-dropdown:hover');
        if (initiallyHoveredItem instanceof HTMLElement) {
          triggerPrimaryMenuPreload(initiallyHoveredItem);
          scheduleHoverOpenIntent(initiallyHoveredItem);
        }
      }

      addRuntimeEventListener(window, 'pageshow', (event) => {
        /* The initial pageshow can arrive well after DOMContentLoaded on a
           media-heavy PDP. Do not let that normal load event close a menu the
           visitor has already opened. Only reset UI restored from bfcache. */
        if (!event.persisted) return;
        clearHoverOpenTimer();
        clearCloseMenuTimer();
        closeActiveDropdown();
      });

      addRuntimeEventListener(window, 'resize', () => {
        clearHoverOpenTimer();
        clearCloseMenuTimer();
        closeActiveDropdown();
        updatePrimaryOpenState();
      }, { passive: true });

      onAbort(() => {
        clearHoverOpenTimer();
        clearCloseMenuTimer();
        clearPrimarySwitchFrame();
        clearPrimaryClosingTimer();
        clearPrimaryBackdropOpenFrame();
      });
    }

    if (typeof shared.schedulePrimaryMenuPreload === 'function') {
      shared.schedulePrimaryMenuPreload();
    }

    onAbort(() => {
      if (navLayoutRaf != null) {
        window.cancelAnimationFrame(navLayoutRaf);
        navLayoutRaf = null;
      }

      if (navLayoutObserver) {
        navLayoutObserver.disconnect();
        navLayoutObserver = null;
      }
    });
  });
})();
